//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import {
  REELS_MAX_ASPECT_RATIO,
  REELS_MAX_VIDEO_DURATION_MILLIS,
  REELS_MAX_VIDEO_SIZE,
  REELS_MIN_ASPECT_RATIO,
  REELS_MIN_VIDEO_DURATION_MILLIS,
} from '@instagram-account/post-details/postDetailsUIConstants';
import { InstagramPostDetailsValidationError } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import {
  getMessageCharacterOccurrenceStatus,
  getMessageHashtagOccurrenceStatus,
} from '@instagram-account/redux/post-details/instagramPostDetailsUtils';
import Cuid from '@models/Cuid';
import { getMediaMetadata, IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefinedWithNetworkItem, isGif } from '@models/INetworkItem';
import { InstagramPublishingStrategy } from '@models/InstagramPublishingStrategy';
import { getMediaAspectRatio } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import { isDefined } from '@utils/filterPredicates';

export const isValidIgReelsAspectRatio = (aspectRatio: number): boolean => {
  return (
    aspectRatio >= REELS_MIN_ASPECT_RATIO &&
    aspectRatio <= REELS_MAX_ASPECT_RATIO
  );
};

export const isValidIgReelsDuration = (
  videoDurationMillis: number,
): boolean => {
  return (
    videoDurationMillis >= REELS_MIN_VIDEO_DURATION_MILLIS &&
    videoDurationMillis <= REELS_MAX_VIDEO_DURATION_MILLIS
  );
};

export const isValidIgReelsSize = (videoSize: number): boolean => {
  return videoSize <= REELS_MAX_VIDEO_SIZE;
};

/**
 * Check for user-fixable errors on Reels post specific form values that
 * will block a user from successfully scheduling the current post.
 */
export const validateInstagramReelsPostFields = ({
  scheduleMediaMap,
  publishingStrategy,
  cuid,
  caption,
}: {
  scheduleMediaMap: PojoMap<Cuid, IMlsMediaItem>;
  publishingStrategy: InstagramPublishingStrategy;
  cuid?: Cuid;
  caption?: string;
}): PojoSet<InstagramPostDetailsValidationError> => {
  let reelsFormErrors = PojoSet.empty<InstagramPostDetailsValidationError>();

  if (!isDefined(cuid)) {
    reelsFormErrors = PojoSet.add(
      reelsFormErrors,
      InstagramPostDetailsValidationError.NoMedia,
    );
  } else if (publishingStrategy === InstagramPublishingStrategy.Automatic) {
    const mediaItem = PojoMap.get(scheduleMediaMap, cuid);

    if (isDefinedWithNetworkItem(mediaItem)) {
      const networkItem = mediaItem.networkItem;

      if (isGif(networkItem)) {
        reelsFormErrors = PojoSet.add(
          reelsFormErrors,
          InstagramPostDetailsValidationError.Gif,
        );
      }

      const mediaMetadata = getMediaMetadata(mediaItem);
      const aspectRatio = getMediaAspectRatio(
        mediaMetadata?.width,
        mediaMetadata?.height,
      );

      if (!isValidIgReelsAspectRatio(aspectRatio)) {
        reelsFormErrors = PojoSet.add(
          reelsFormErrors,
          InstagramPostDetailsValidationError.InvalidMediaAspectRatio,
        );
      }

      const videoDuration = mediaMetadata?.durationMillis;
      if (isDefined(videoDuration) && !isValidIgReelsDuration(videoDuration)) {
        reelsFormErrors = PojoSet.add(
          reelsFormErrors,
          InstagramPostDetailsValidationError.InvalidVideoLength,
        );
      }

      const videoSize = mediaMetadata?.fileSize;
      if (isDefined(videoSize) && !isValidIgReelsSize(videoSize)) {
        reelsFormErrors = PojoSet.add(
          reelsFormErrors,
          InstagramPostDetailsValidationError.InvalidReelsVideoSize,
        );
      }
    }
  }

  // Caption must be valid length and not have too many hashtags
  if (isDefined(caption)) {
    if (getMessageHashtagOccurrenceStatus(caption).isAboveLimit) {
      reelsFormErrors = PojoSet.add(
        reelsFormErrors,
        InstagramPostDetailsValidationError.TooManyHashtags,
      );
    } else if (getMessageCharacterOccurrenceStatus(caption).isAboveLimit) {
      reelsFormErrors = PojoSet.add(
        reelsFormErrors,
        InstagramPostDetailsValidationError.CaptionTooLong,
      );
    }
  }

  return reelsFormErrors;
};
